import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import type { ActionModuleEmployeeResponse, ActionModuleInstanceIncentivationRequest, ActionModuleInstanceModel, OrderType } from '@/api/model'
import { AccountStatus } from '@/api/model'
import { IncentivationDataType } from '@/api/model/incentivationDataType'
import { Card } from '@/components/shadcn/card'
import { cn } from '@/lib/utils'
import { notify } from '@/lib/notify'
import { PaginationFooter } from '@/components/common/table/PaginationFooter'
import {
  useGetApiCompaniesCurrentActionmodulesActionModuleIdEmployees,
  useGetApiCompaniesCurrentActionmodulesActionModuleIdEmployeesFilter,
  usePatchApiCompaniesCurrentActionmodulesActionModuleIdIncentivation,
} from '@/api/generated/company-management-user/company-management-user'
import { getWeekRange, toIso } from '@/pages/incentivation/lib'
import { useActionModuleDetail } from '../ActionModuleDetailPage'
import { moduleIncentivationDataType } from '../lib'
import { moduleCanPlotScore } from './chart'
import { IncentivationFilters, type IncentivationFilterValues } from './IncentivationFilters'
import { EmployeeColumn } from './EmployeeColumn'
import { InstanceColumn } from './InstanceColumn'
import { ScoreDevelopmentChart } from './ScoreDevelopmentChart'
import { InstanceDetailsColumn } from './InstanceDetailsColumn'
import { useInstances } from './useInstances'
import type { RewardCalculationInformation } from './ScoreEditCell'

const { start: DEFAULT_START, end: DEFAULT_END } = getWeekRange()
const DEFAULT_PAGE_SIZE = 20

/** Identifies a selected instance by its period, so it can be re-matched across employees. */
interface InstanceKey {
  start: string
  end: string
}

/**
 * Incentivierung tab — read-only Miller-column drill-down (employee → instance →
 * details) rendered as a *single* framed unit: columns are panels separated by thin
 * dividers with a soft left shadow, all sharing one full height, with one pagination bar
 * spanning the whole width (it paginates the employee list).
 *
 * The instance selection is stored by *period* (start/end), not id, so it survives an
 * employee change: switching employees keeps the same-period instance of the new employee
 * selected (matched from the shared, cached instances query) — no re-click needed.
 */
export default function IncentivationTab() {
  const { t } = useTranslation('action-modules-detail')
  const { module } = useActionModuleDetail()
  const moduleId = module.id ?? 0
  const queryClient = useQueryClient()
  const dataType = moduleIncentivationDataType(module)
  // No editable score for None-type modules → the edit toggle is not offered.
  const canEdit = dataType !== IncentivationDataType.None

  const [applied, setApplied] = useState<IncentivationFilterValues>({
    start: DEFAULT_START,
    end: DEFAULT_END,
    search: '',
    groupIds: [],
    instanceStatuses: [],
  })

  // Only the groups this module is assigned to are offered as filter options
  const { data: filterResponse } = useGetApiCompaniesCurrentActionmodulesActionModuleIdEmployeesFilter(moduleId)
  const groupOptions = filterResponse?.data?.groups ?? []
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [sort, setSort] = useState<string | undefined>(undefined)
  const [order, setOrder] = useState<OrderType | undefined>(undefined)

  const [selectedEmployee, setSelectedEmployee] = useState<ActionModuleEmployeeResponse | null>(null)
  const [selectedInstanceKey, setSelectedInstanceKey] = useState<InstanceKey | null>(null)

  // Edit mode: drafts keyed by instance id survive pagination, sort & selection. Keying by
  // instance (not employee) lets one employee carry several edited instances (their last
  // instance from the table + older instances from the drill-down).
  const [editMode, setEditMode] = useState(false)
  const [drafts, setDrafts] = useState<Map<number, ActionModuleInstanceIncentivationRequest>>(new Map())

  const { mutate: saveDrafts, isPending: isSaving } = usePatchApiCompaniesCurrentActionmodulesActionModuleIdIncentivation()

  const { data, isLoading, isError } = useGetApiCompaniesCurrentActionmodulesActionModuleIdEmployees(moduleId, {
    Offset: (page - 1) * pageSize,
    Limit: pageSize,
    Sort: sort,
    Order: order,
    Search: applied.search || undefined,
    GroupIds: applied.groupIds.length > 0 ? applied.groupIds : undefined,
    InstanceStatuses: applied.instanceStatuses.length > 0 ? applied.instanceStatuses : undefined,
    StartDate: toIso(applied.start),
    EndDate: toIso(applied.end, true),
  }, {
    // Both dates are required by the endpoint — don't fire a request that can only 400
    query: { enabled: !!applied.start && !!applied.end },
  })
  const raw = (data as any)?.data ?? data
  const employees: ActionModuleEmployeeResponse[] = raw?.items ?? []
  const totalCount: number = raw?.totalCount ?? 0

  // Selected employee's instances (shared cache with the columns). Used here to resolve
  // the period-based selection into the concrete instance of the current employee.
  const { instances } = useInstances(moduleId, selectedEmployee?.id ?? '', applied, {
    enabled: !!selectedEmployee?.id,
  })
  const selectedInstance = selectedInstanceKey
    ? instances.find((inst) => inst.startDate === selectedInstanceKey.start && inst.endDate === selectedInstanceKey.end) ?? null
    : null
  const selectedInstanceId = selectedInstance?.id ?? null

  // Every filter change refetches immediately (no apply button) and jumps back to page 1.
  function changeFilters(patch: Partial<IncentivationFilterValues>) {
    setApplied((prev) => ({ ...prev, ...patch }))
    setPage(1)
    // A changed filter means a different set of instances — drop the drill-down selection.
    setSelectedEmployee(null)
    setSelectedInstanceKey(null)
  }

  function handleSort(field: string | undefined, ord: OrderType | undefined) {
    setSort(field); setOrder(ord); setPage(1)
  }

  // Changing the employee deliberately keeps the instance-period selection: the matching
  // instance of the new employee (if any) stays selected.
  function selectEmployee(employee: ActionModuleEmployeeResponse) {
    setSelectedEmployee((prev) => (prev?.id === employee.id ? null : employee))
  }

  function selectInstance(instance: ActionModuleInstanceModel) {
    setSelectedInstanceKey((prev) =>
      prev && prev.start === instance.startDate && prev.end === instance.endDate
        ? null
        : { start: instance.startDate, end: instance.endDate },
    )
  }

  function enterEditMode() {
    // Editing works at the top level and in the drill-down — keep any current selection so the
    // user can edit the instance they are already looking at.
    setEditMode(true)
  }

  function cancelEditMode() {
    setDrafts(new Map())
    setEditMode(false)
  }

  // Upsert a dirty row's request into the drafts, or remove it when the value reverts to the
  // original (info === null). Keyed by instance id → last-write-wins across pagination and
  // across the two editing surfaces (table row + details pane).
  function handleDraftChange(employeeUserId: string, instanceId: number, info: RewardCalculationInformation | null) {
    setDrafts((prev) => {
      const next = new Map(prev)
      if (info === null) {
        next.delete(instanceId)
      } else {
        next.set(instanceId, { employeeUserId, instanceId, rewardCalculationInformation: info })
      }
      return next
    })
  }

  function handleSave() {
    if (drafts.size === 0) {
      cancelEditMode();
    }

    saveDrafts({ actionModuleId: moduleId, data: [...drafts.values()] }, {
      onSuccess: () => {
        notify.success(t('incentivation.edit.saveSuccess'))
        // Refresh the whole module in one sweep: the trailing slash covers both the employees
        // list (all pages → Score + Points) and the per-employee instances (list + details pane
        // + chart), so any still-open drill-down re-renders from the server too.
        const prefix = `/api/companies/current/actionmodules/${moduleId}/`
        queryClient.invalidateQueries({
          predicate: (q) => typeof q.queryKey[0] === 'string' && (q.queryKey[0] as string).startsWith(prefix),
        })
        setDrafts(new Map())
        setEditMode(false)
      },
      // Stay in edit mode with drafts intact so the user can retry.
      onError: (error) => notify.apiError(error, t('incentivation.edit.saveError')),
    })
  }

  const employeeSelected = selectedEmployee != null
  const instanceSelected = selectedInstanceId != null
  const showGraph = employeeSelected && !instanceSelected && moduleCanPlotScore(module)

  // Panels opened by a selection sit above their left neighbour with a soft left shadow,
  // so the drill-down reads as layered depth rather than flat side-by-side tables.
  const openPanel = 'relative z-10 min-h-full bg-card shadow-[-8px_0_12px_-8px_rgba(0,0,0,0.18)]'

  return (
    <div className="mt-4 flex min-h-0 flex-1 flex-col gap-4">
      <IncentivationFilters
        moduleId={moduleId}
        values={applied}
        onChange={changeFilters}
        groupOptions={groupOptions}
        sort={sort}
        order={order}
        canEdit={canEdit}
        editMode={editMode}
        isSaving={isSaving}
        onEnterEdit={enterEditMode}
        onSave={handleSave}
        onCancel={cancelEditMode}
      />

      {/* One framed unit: full height, panels separated by thin dividers + a left shadow,
          one pagination bar spanning the whole width. The panel row is the single scroll
          area (both axes, table headers scroll along); the pagination bar sits outside it
          and stays put. Each panel carries a min width so narrow screens scroll
          horizontally instead of squeezing the tables. */}
      <Card className="flex min-h-0 flex-1 flex-col gap-0 overflow-hidden p-0">
        <div className="flex min-h-0 flex-1 items-stretch divide-x divide-border overflow-auto">
          <div className={cn('min-h-full', employeeSelected ? 'w-80 shrink-0' : 'min-w-[46rem] flex-1')}>
            <EmployeeColumn
              employees={employees}
              isLoading={isLoading}
              isError={isError}
              sort={sort}
              order={order}
              onSort={handleSort}
              selectedEmployeeId={selectedEmployee?.id ?? null}
              onSelectEmployee={selectEmployee}
              compact={employeeSelected}
              editMode={editMode}
              dataType={dataType}
              drafts={drafts}
              onDraftChange={handleDraftChange}
            />
          </div>

          {employeeSelected && selectedEmployee?.id && (
            <div className={cn(openPanel, instanceSelected ? 'w-96 shrink-0' : 'min-w-[28rem] flex-1')}>
              <InstanceColumn
                moduleId={moduleId}
                employeeId={selectedEmployee.id}
                applied={applied}
                selectedInstanceId={selectedInstanceId}
                onSelectInstance={selectInstance}
                compact={instanceSelected}
                onClose={() => setSelectedEmployee(null)}
              />
            </div>
          )}

          {showGraph && selectedEmployee?.id && (
            <div className={cn(openPanel, 'min-w-[20rem] flex-1')}>
              <ScoreDevelopmentChart moduleId={moduleId} employeeId={selectedEmployee.id} applied={applied} />
            </div>
          )}

          {instanceSelected && selectedEmployee?.id && selectedInstanceId != null && (
            <div className={cn(openPanel, 'min-w-[24rem] flex-1')}>
              <InstanceDetailsColumn
                module={module}
                employeeId={selectedEmployee.id}
                instanceId={selectedInstanceId}
                applied={applied}
                // Any instance of an active employee is editable (backend decides the rest).
                editMode={editMode && selectedEmployee.status === AccountStatus.Active}
                dataType={dataType}
                draft={drafts.get(selectedInstanceId)?.rewardCalculationInformation ?? undefined}
                onDraftChange={handleDraftChange}
                onClose={() => setSelectedInstanceKey(null)}
              />
            </div>
          )}
        </div>

        {/* Single pagination bar spanning the whole unit (paginates the employee list). */}
        <PaginationFooter
          offset={(page - 1) * pageSize}
          limit={pageSize}
          totalCount={totalCount}
          onOffsetChange={(offset) => setPage(Math.floor(offset / pageSize) + 1)}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1) }}
        />
      </Card>
    </div>
  )
}
